// An agent's before and after screenshots (docs/factory.md "Screenshots and
// What's new"): blob ids on the AgentRun mirror, fetched behind the token like
// any blob picture (lib/blobUrls.ts). One shot is a pair, or an after alone
// for a screen the base did not have ("New"). ShotFlip is one frame with a
// Before/After switch, After first (a phone card, the PR card); ShotSideBySide
// puts the two next to each other (a desktop). RunShots is the block the
// review page and the PR card share.
import { useState } from 'react'
import { api } from '@/runtime'
import type { AgentRunResource } from '@/kinds/agentrun/apply'
import type { RunShot } from '@/kinds/agentrun/schema'
import { BLOB_ID } from '@/lib/blobs'
import { createBlobUrls } from '@/lib/blobUrls'
import { useBlobUrls } from '@/hooks/useBlobUrls'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { PictureViewer } from '@/components/MessagePictures'
import { Skeleton } from '@/components/Skeleton'
import { Segmented, Tag, cx } from '@/components/ui'

export type ShotSide = 'after' | 'before'

// one URL per blob, shared by every view of the shots; past the cap the ones
// nothing shows are let go
const urls = createBlobUrls(32)
const load = (id: string) => api.blob(id)

const SIDES: { value: ShotSide; label: string }[] = [
  { value: 'after', label: 'After' },
  { value: 'before', label: 'Before' },
]

/** The frame a shot takes when the caller does not size it: a phone screen is tall, a desktop one wide. */
export const defaultFrame = (shot: RunShot) =>
  shot.viewport === 'desktop'
    ? 'w-full aspect-[1440/900]'
    : 'w-full max-w-[22rem] mx-auto h-[min(45vh,22rem)]'

const useShotUrls = (shot: RunShot) => {
  const ids = [shot.after, shot.before ?? ''].filter((id) => BLOB_ID.test(id))
  return useBlobUrls(urls, ids, load)
}

/** Why a picture is not there yet: offline, or the server would not give it. */
const Missing = ({ online }: { online: boolean }) => (
  <p className="text-xs text-faint mt-1" data-testid="shot-missing">
    {online ? 'This picture could not be loaded.' : 'The pictures show when you are online.'}
  </p>
)

/**
 * The pictures in a box the caller sizes: each centred and as large as fits,
 * hugging its own edge; only the chosen side shows (a crossfade between them).
 */
const Frame = ({
  shot,
  side,
  got,
  broken,
  className,
  onOpen,
  single = false,
}: {
  shot: RunShot
  side: ShotSide
  got: Record<string, string>
  broken: (id: string) => void
  className: string
  onOpen: (src: string) => void
  /** only the chosen side (side by side), not both stacked for the switch */
  single?: boolean
}) => {
  const layers = [
    { id: shot.after, side: 'after' as const },
    ...(shot.before ? [{ id: shot.before, side: 'before' as const }] : []),
  ].filter((l) => !single || l.side === side)
  const shown = got[side === 'before' && shot.before ? shot.before : shot.after]
  return (
    <div className={cx('relative', className)}>
      {!shown && <Skeleton className="absolute inset-0" />}
      {layers.map((l) =>
        got[l.id] ? (
          <img
            key={l.id}
            src={got[l.id]}
            onError={() => broken(l.id)}
            alt={
              l.side === side
                ? `${shot.caption || shot.name}, ${l.side === 'after' ? 'after' : 'before'}`
                : ''
            }
            aria-hidden={l.side !== side}
            draggable={false}
            onClick={() => onOpen(got[l.id]!)}
            className={cx(
              'absolute inset-0 m-auto cursor-zoom-in border border-line transition-opacity duration-200',
              // a phone screen is read from its top at a readable size (a tap shows all of it);
              // shrunk whole into the frame it was a tall dark strip of unreadable words
              shot.viewport === 'desktop' ? 'max-h-full max-w-full' : 'w-full h-full object-cover object-top',
              l.side === side ? 'opacity-100' : 'opacity-0 pointer-events-none',
            )}
            style={{ borderRadius: 'var(--radius-card-inner)' }}
            data-testid={`shot-${l.side}`}
          />
        ) : null,
      )}
    </div>
  )
}

/** One shot in one frame with a Before/After switch (After first), or "New" when there is no before. */
export const ShotFlip = ({
  shot,
  frame,
  className,
}: {
  shot: RunShot
  frame?: string
  className?: string
}) => {
  const [side, setSide] = useState<ShotSide>('after')
  const [open, setOpen] = useState('')
  const { urls: got, online, failed, broken } = useShotUrls(shot)
  return (
    <figure
      className={cx('flex flex-col min-h-0', className)}
      data-testid="shot"
      data-name={shot.name}
      data-side={side}
    >
      <div className="flex items-center gap-2 mb-2 shrink-0 min-h-8">
        {shot.before ? (
          <Segmented value={side} options={SIDES} onChange={setSide} label="Before or after" />
        ) : (
          <Tag tone="accent">New</Tag>
        )}
        {shot.caption && (
          <figcaption className="min-w-0 flex-1 text-[13px] text-muted leading-snug line-clamp-2">
            {shot.caption}
          </figcaption>
        )}
      </div>
      <Frame
        shot={shot}
        side={side}
        got={got}
        broken={broken}
        className={frame ?? defaultFrame(shot)}
        onOpen={setOpen}
      />
      {failed && !got[shot.after] && <Missing online={online} />}
      {open && <PictureViewer src={open} onClose={() => setOpen('')} label={shot.caption || 'Screenshot'} />}
    </figure>
  )
}

/** One shot with before and after next to each other; a new screen alone, across both. */
export const ShotSideBySide = ({ shot }: { shot: RunShot }) => {
  const [open, setOpen] = useState('')
  const { urls: got, online, failed, broken } = useShotUrls(shot)
  const frame = shot.viewport === 'desktop' ? 'w-full aspect-[1440/900]' : 'w-full h-[min(64vh,600px)]'
  const half = (side: ShotSide, label: string) => (
    <div className="min-w-0">
      <div className="label text-muted mb-1.5">{label}</div>
      <Frame shot={shot} side={side} got={got} broken={broken} className={frame} onOpen={setOpen} single />
    </div>
  )
  return (
    <figure data-testid="shot" data-name={shot.name}>
      {shot.before ? (
        <div className="grid grid-cols-2 gap-4">
          {half('before', 'Before')}
          {half('after', 'After')}
        </div>
      ) : (
        <div className={shot.viewport === 'desktop' ? 'max-w-3xl' : ''}>{half('after', 'New')}</div>
      )}
      {shot.caption && <figcaption className="text-sm text-muted mt-2">{shot.caption}</figcaption>}
      {failed && !got[shot.after] && <Missing online={online} />}
      {open && <PictureViewer src={open} onClose={() => setOpen('')} label={shot.caption || 'Screenshot'} />}
    </figure>
  )
}

/** A run's shots, side by side on a wide screen and one frame each otherwise (`narrow` keeps frames). */
export const RunShots = ({ run, narrow = false }: { run: AgentRunResource; narrow?: boolean }) => {
  const wide = useMediaQuery('(min-width: 1024px)')
  const shots = (run.spec.shots ?? []).filter((s) => !!s.after)
  if (!shots.length) return null
  const side = wide && !narrow
  return (
    <div className="flex flex-col gap-5" data-testid="run-shots">
      {shots.map((s) =>
        side ? (
          <ShotSideBySide key={s.name} shot={s} />
        ) : (
          <ShotFlip
            key={s.name}
            shot={s}
            frame={s.viewport === 'desktop' ? 'w-full aspect-[1440/900]' : 'w-full h-[min(56vh,480px)]'}
          />
        ),
      )}
    </div>
  )
}
